import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {RootState} from "../../../reducers";
import {getStoreConfig} from "../../services/Store/reducer";
import ErrorFormHelper from "../../utils/ErrorFormHelper";
import ErrorFormAlertView from "../Common/ErrorFormAlertView";
import TpayBankTransferPaymentMethodComponent
    from "./components/PaymentMethod/tpay/TpayBankTransferPaymentMethodComponent";
import BluemediaBankTransferPaymentMethodComponent
    from "./components/PaymentMethod/bluemedia/BluemediaBankTransferPaymentMethodComponent";
import CartHelper from "../../services/Cart/Helper/CartHelper";
import {getCart} from "../../services/Cart/reducer";
import {CartState} from "../../services/Cart/types";
import {StoreConfigPaymentMethodApi} from "../../services/Api/types";
import {createKeyDownHandler} from "../../utils/keyDownHanlder";
import autopayIcon from "../../../images/icons/payments/autopay.svg";
import tpayIcon from "../../../images/icons/payments/tpay.svg";
import cashIcon from "../../../images/icons/payments/cash.svg";
import cardIcon from "../../../images/icons/payments/card.svg";
import applePayIcon from "../../../images/icons/payments/applepay.svg";
import googlePayIcon from "../../../images/icons/payments/googlepay.svg";
import blikIcon from "../../../images/icons/payments/blik.svg";
import Utils from "../../utils/Utils";

interface OwnProps {
    onChange: (cart: any) => void;
    data: CartState;
    errors: any[];
}


declare global {
    interface Window {
        ApplePaySession?: typeof ApplePaySession;
    }

    class ApplePaySession {
        constructor(version: number, request: any);

        static canMakePayments(): boolean;
        static canMakePaymentsWithActiveCard(merchantIdentifier: string): Promise<boolean>;

        static STATUS_SUCCESS: number;
        static STATUS_FAILURE: number;

        onvalidatemerchant: (event: any) => void;
        onpaymentauthorized: (event: any) => void;

        begin(): void;
        completeMerchantValidation(merchantSession: object): void;
        completePayment(status: number): void;
        abort(): void;
        oncancel: (event: Event) => void;
    }
}

const checkIfApplePayIsAvailable = () => {
    if(!Utils.isSafari()) return false;
    if (window.ApplePaySession) {
        try {
            const value = window.ApplePaySession.canMakePayments();

            if (value) {
                return true;
            }
        } catch (err) {
            return false;
        }
    }
    return false;
};

const checkIfGooglePayIsAvailable = () => {
    return Utils.isAndroid() && Utils.isChromeLike();
};
type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;
const CartPaymentComponent = (props: Props) => {
    const cart = props.cart;
    const [paymentMethodId, setPaymentMethodId] = useState(CartHelper.getDefaultPaymentMethodId(cart.payment_method_id, CartHelper.getAvailablePaymentMethods(props.config?.ordering?.payment_methods ? props.config?.ordering?.payment_methods : [], cart.type)));
    const [paymentMethodGatewayId, setPaymentMethodGatewayId] = useState<string | undefined>(cart.payment_settings.gateway_id ? cart.payment_settings.gateway_id : undefined);
    const {t} = useTranslation();

    const availablePaymentMethods = CartHelper.getAvailablePaymentMethods(props.config?.ordering?.payment_methods ? props.config?.ordering?.payment_methods : [], cart.type);

    const setPaymentMethod = (paymentMethod: any) => {
        setPaymentMethodId(paymentMethod.id);
        props.onChange({
            ...props.data,
            payment_method_id: paymentMethod.id
        })
    }

    const updatePaymentSettings = (settings: any) => {
        props.onChange({
            ...props.data,
            payment_settings: settings
        })
    }

    const updatePaymentMethodAndGatewayId = (paymentMethod: StoreConfigPaymentMethodApi, gatewayId: string) => {
        setPaymentMethodId(paymentMethod.id);
        setPaymentMethodGatewayId(gatewayId)
        if(gatewayId === "0"){
            props.onChange({
                ...props.data,
                payment_method_id: paymentMethod.id
            })
        }else{
            props.onChange({
                ...props.data,
                payment_method_id: paymentMethod.id,
                payment_settings: {gateway_id: gatewayId}
            })
        }
    }

    const getPaymentMethod = (paymentMethodId?: string, paymentMethods?: StoreConfigPaymentMethodApi[]) => {
        if (!paymentMethodId) {
            return undefined;
        }
        if (!paymentMethods) return undefined;

        let paymentMethod = paymentMethods.filter((paymentMethod: any) => {
            return paymentMethod.id === paymentMethodId;
        })[0];
        if (paymentMethod !== undefined && paymentMethod) {
            return paymentMethod;
        }
        return undefined;
    }

    const selectedPaymentMethod = getPaymentMethod(paymentMethodId, availablePaymentMethods);

    const handleOnKeyDown = (paymentMethod: StoreConfigPaymentMethodApi) => createKeyDownHandler<HTMLLabelElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: () => setPaymentMethod(paymentMethod)
    });

    const getPaymentIcon = (paymentMethod: any, gatewayId?: string) => {
        let icon: JSX.Element | null = null;

        if (paymentMethod.gateway === "BLUEMEDIA") {
            icon = <img src={autopayIcon} alt="Autopay"/>;
        }
        if (paymentMethod.gateway === "TPAY") {
            if(!gatewayId){
                icon = <img src={tpayIcon} alt="Tpay"/>;
            }else{
                switch (gatewayId) {
                    case "170":
                        icon = <img src={applePayIcon} alt="Apple Pay"/>;
                        break;
                    case "166":
                        icon = <img src={googlePayIcon} alt="Google Pay"/>;
                        break;
                    case "150":
                        icon = <img src={blikIcon} alt="Blik Pay"/>;
                        break;
                    default:
                        icon = <img src={tpayIcon} alt="Tpay"/>;
                        break;
                }
            }
        }

        if (!paymentMethod.gateway) {
            if (paymentMethod.name.toLowerCase().includes("cash") || paymentMethod.name.toLowerCase().includes("gotówka")) {
                icon = <img src={cashIcon} alt="Cash"/>;
            }
            if (paymentMethod.name.toLowerCase().includes("card") || paymentMethod.name.toLowerCase().includes("karta")) {
                icon = <img src={cardIcon} alt="Card"/>;
            }
        }

        if (!icon) return null;

        return (
            <div className="payment-method-icon-container">
                {icon}
            </div>
        );
    }

    const renderPaymentMethodGateway = (paymentMethod: StoreConfigPaymentMethodApi, name: string, description: string, gatewayId: string) => {
        return (
            <>
                <label tabIndex={0}
                       onKeyDown={handleOnKeyDown(paymentMethod)}
                       className={`btn btn-default btn-lg btn-check-full${paymentMethodId === paymentMethod.id && gatewayId == paymentMethodGatewayId ? " active" : ""}`}>
                                                <span>
                                                    {name}
                                                    <small>
                                                        {description}
                                                    </small>
                                                </span>
                    {getPaymentIcon(paymentMethod, gatewayId)}
                    <input className="btn-check1" type="radio" name="paymentMethodId"
                           value={paymentMethod.id + "_" + gatewayId}
                           autoComplete="off" tabIndex={-1}
                           checked={paymentMethodId === paymentMethod.id && gatewayId == paymentMethodGatewayId}
                           onChange={() => updatePaymentMethodAndGatewayId(paymentMethod, gatewayId)}/>
                </label>
                {(gatewayId === "0" && gatewayId == paymentMethodGatewayId) && (
                    <TpayBankTransferPaymentMethodComponent
                        updatePaymentSettings={updatePaymentSettings}
                        paymentMethod={paymentMethod}/>
                )}
            </>
        )
    }
    const renderPaymentMethod = (paymentMethod: StoreConfigPaymentMethodApi) => {
        if (paymentMethod.payment_types !== undefined && paymentMethod.payment_types.length > 0) {
            const paymentTypesView = [] as any[];
            if (paymentMethod.payment_types.includes("APPLE_PAY") && checkIfApplePayIsAvailable()) {
                paymentTypesView.push(renderPaymentMethodGateway(paymentMethod, t("modules.cart.payment.payment_types.title.apple_pay"), t("modules.cart.payment.payment_types.description.payment_in_advance"), "170"))
            }
            if (paymentMethod.payment_types.includes("GOOGLE_PAY") && checkIfGooglePayIsAvailable()) {
                paymentTypesView.push(renderPaymentMethodGateway(paymentMethod, t("modules.cart.payment.payment_types.title.google_pay"), t("modules.cart.payment.payment_types.description.payment_in_advance"), "166"))
            }
            if (paymentMethod.payment_types.includes("BLIK")) {
                paymentTypesView.push(renderPaymentMethodGateway(paymentMethod, t("modules.cart.payment.payment_types.title.blik"), t("modules.cart.payment.payment_types.description.payment_in_advance"), "150"))
            }
            if (paymentMethod.payment_types.includes("E_TRANSFER")) {
                paymentTypesView.push(renderPaymentMethodGateway(paymentMethod, t("modules.cart.payment.payment_types.title.e_transfer"), t("modules.cart.payment.payment_types.description.payment_in_advance"), "0"))
            }
            return (
                <>
                    {paymentTypesView.map(paymentTypeView => {
                        return (
                            <>
                                {paymentTypeView}
                            </>
                        )
                    })}
                </>
            )
        }
        return (
            <React.Fragment key={paymentMethod.id}>
                <label tabIndex={0}
                       onKeyDown={handleOnKeyDown(paymentMethod)}
                       className={`btn btn-default btn-lg btn-check-full${paymentMethodId === paymentMethod.id ? " active" : ""}`}>
                                                <span>
                                                    {paymentMethod.name}
                                                    <small>
                                                        {paymentMethod.description}
                                                    </small>
                                                </span>
                    {getPaymentIcon(paymentMethod)}
                    <input className="btn-check1" type="radio" name="paymentMethodId"
                           value={paymentMethod.id}
                           autoComplete="off" tabIndex={-1}
                           checked={paymentMethodId === paymentMethod.id}
                           onChange={() => setPaymentMethod(paymentMethod)}/>
                </label>
                {(selectedPaymentMethod && selectedPaymentMethod.id === paymentMethod.id) && (
                    <>
                        {selectedPaymentMethod.gateway === "BLUEMEDIA" && (
                            <BluemediaBankTransferPaymentMethodComponent
                                updatePaymentSettings={updatePaymentSettings}
                                paymentMethod={paymentMethod}/>
                        )}
                        {(selectedPaymentMethod.gateway === "TPAY") && (
                            <TpayBankTransferPaymentMethodComponent
                                updatePaymentSettings={updatePaymentSettings}
                                paymentMethod={paymentMethod}/>
                        )}
                    </>
                )}
            </React.Fragment>
        )
    }

    return (
        <div className="card-cart cart-payments">
            <div className={"container-fluid"}>
                <h5 className={"card-cart-header"}>
                    {t("modules.cart.payment.header.title")}
                </h5>
                <div className={"card-cart-body"}>
                    <div
                        className={"" + ErrorFormHelper(props.errors, "paymentMethodId,paymentMethod")}>
                        <ErrorFormAlertView errors={props.errors} field="paymentMethodId"/>
                        <ErrorFormAlertView errors={props.errors} field="paymentMethod"/>
                        <div className={"btn-groups-columns"}>
                            {
                                availablePaymentMethods.map((paymentMethod: any) => {
                                        return renderPaymentMethod(paymentMethod)
                                    }
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    cart: getCart(state.cart)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartPaymentComponent));